import { AppIcon } from '../ui/icon'
import { mdiAccount, mdiMapMarker  } from '@mdi/js'
import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { $api } from '../../api/queryClient'
import { getLocalizedName } from '../../lib/localization'
import { SECTION_ICONS } from '../../lib/section-icons'
import { issueTargetLabel } from './issueSources'

export type TargetType = 'asset' | 'user' | 'contract' | 'document' | 'asset_representation' | 'intervention'

/**
 * The label naming a ticket's target by what it is (spec 2026-10-11-26): the
 * asset's or document's type name (« Immeuble », « Règlement de copropriété »),
 * else the target entity type. The asset and document reads share their query
 * keys with IssueTargetLink, so the label costs only the type read.
 */
export function useIssueTargetLabel(orgCode: string, type: string | null | undefined, uid: string | null | undefined) {
  const { t, i18n } = useTranslation('issues')
  const isAsset = type === 'asset' && Boolean(uid)
  const isDocument = type === 'document' && Boolean(uid)
  const asset = $api.useQuery(
    'get',
    '/api/v1/organizations/{orgCode}/assets/{assetUid}',
    { params: { path: { orgCode, assetUid: uid ?? '' } } },
    { enabled: isAsset },
  )
  const doc = $api.useQuery(
    'get',
    '/api/v1/organizations/{orgCode}/documents/{documentUid}',
    { params: { path: { orgCode, documentUid: uid ?? '' } } },
    { enabled: isDocument },
  )
  const assetTypeUid = isAsset ? asset.data?.asset_type_uid : undefined
  const documentTypeUid = isDocument ? doc.data?.document_type_uid : undefined
  // There is no single-type read: the list is the one useAssetTypes(org)
  // caches for every asset picker, so it is usually already loaded.
  const assetTypes = $api.useQuery(
    'get',
    '/api/v1/organizations/{orgCode}/asset-types',
    { params: { path: { orgCode }, query: { limit: 200 } } },
    { enabled: Boolean(assetTypeUid) },
  )
  const documentType = $api.useQuery(
    'get',
    '/api/v1/organizations/{orgCode}/document-types/{documentTypeUid}',
    { params: { path: { orgCode, documentTypeUid: documentTypeUid ?? '' } } },
    { enabled: Boolean(documentTypeUid) },
  )
  type TypeRow = { uid?: string; name?: unknown }
  const listBody = assetTypes.data as { data?: TypeRow[] | null } | TypeRow[] | undefined
  const assetTypeList = Array.isArray(listBody) ? listBody : listBody?.data
  const rawName: unknown = assetTypeUid
    ? assetTypeList?.find((type) => type.uid === assetTypeUid)?.name
    : documentTypeUid
      ? documentType.data?.name
      : undefined
  const typeName =
    typeof rawName === 'string'
      ? rawName
      : getLocalizedName(rawName as Record<string, string> | undefined, i18n.language)
  return issueTargetLabel(type, typeName, t)
}

type Props = {
  orgCode: string
  type: TargetType
  uid: string
  className?: string
  /**
   * Inline pill-style rendering for use in list rows.
   * Hides while the target name is loading to avoid showing raw UIDs.
   */
  compact?: boolean
}

export function IssueTargetLink({ orgCode, type, uid, className, compact }: Props) {
  const { t } = useTranslation('issues')
  const isAsset = type === 'asset'
  const isUser = type === 'user'
  const isContract = type === 'contract'
  const isDocument = type === 'document'
  const isRepresentation = type === 'asset_representation'
  const isIntervention = type === 'intervention'

  const asset = $api.useQuery(
    'get',
    '/api/v1/organizations/{orgCode}/assets/{assetUid}',
    { params: { path: { orgCode, assetUid: uid } } },
    { enabled: isAsset },
  )
  const user = $api.useQuery(
    'get',
    '/api/v1/organizations/{orgCode}/users/{userUid}',
    { params: { path: { orgCode, userUid: uid } } },
    { enabled: isUser },
  )
  const contract = $api.useQuery(
    'get',
    '/api/v1/organizations/{orgCode}/contracts/{contractUid}',
    { params: { path: { orgCode, contractUid: uid } } },
    { enabled: isContract },
  )
  const doc = $api.useQuery(
    'get',
    '/api/v1/organizations/{orgCode}/documents/{documentUid}',
    { params: { path: { orgCode, documentUid: uid } } },
    { enabled: isDocument },
  )
  const intervention = $api.useQuery(
    'get',
    '/api/v1/organizations/{orgCode}/interventions/{interventionUid}',
    { params: { path: { orgCode, interventionUid: uid } } },
    { enabled: isIntervention },
  )
  const rep = $api.useQuery(
    'get',
    '/api/v1/organizations/{orgCode}/representations/{repUid}',
    { params: { path: { orgCode, repUid: uid } } },
    { enabled: isRepresentation },
  )
  // For a representation target the link must navigate to the *parent asset*
  // plan tab, so we resolve the asset once we know its UID.
  const repAssetUid = rep.data?.asset_uid
  const repAsset = $api.useQuery(
    'get',
    '/api/v1/organizations/{orgCode}/assets/{assetUid}',
    { params: { path: { orgCode, assetUid: repAssetUid ?? '' } } },
    { enabled: isRepresentation && Boolean(repAssetUid) },
  )

  let icon = SECTION_ICONS.assets
  let name: string | undefined
  let to: string | undefined
  let params: Record<string, string> | undefined
  let search: Record<string, string> | undefined

  if (isAsset) {
    icon = SECTION_ICONS.assets
    name = asset.data?.name
    to = '/$org/assets/$assetUid'
    params = { org: orgCode, assetUid: uid }
    // Approval issues used to deep-link to the asset's approval section, which
    // has moved into the onboarding wizard (spec
    // 2026-07-09-fiabilisation-onboarding-only). The link now lands on the
    // asset's default view; the resume-onboarding banner on the building-group
    // page is the recall path for validating extracted data.
  } else if (isUser) {
    icon = mdiAccount
    name = user.data?.name || user.data?.email
    to = '/$org/users/$userUid'
    params = { org: orgCode, userUid: uid }
  } else if (isContract) {
    icon = SECTION_ICONS.contracts
    name = contract.data?.name
    to = '/$org/contracts/$contractUid'
    params = { org: orgCode, contractUid: uid }
  } else if (isDocument) {
    icon = SECTION_ICONS.documents
    name = doc.data?.name
    to = '/$org/documents/$docUid'
    params = { org: orgCode, docUid: uid }
  } else if (isIntervention) {
    icon = SECTION_ICONS.interventions
    name = intervention.data?.title
    to = '/$org/interventions/$interventionUid'
    params = { org: orgCode, interventionUid: uid }
  } else if (isRepresentation) {
    icon = mdiMapMarker
    const repTitle = (rep.data?.value as { title?: string } | undefined)?.title
    const assetName = repAsset.data?.name
    name = assetName && repTitle ? `${assetName} — ${repTitle}` : (assetName || repTitle)
    if (repAssetUid) {
      to = '/$org/assets/$assetUid'
      params = { org: orgCode, assetUid: repAssetUid }
      search = { tab: 'plan', rep: uid }
    }
  }

  if (compact) {
    // Hide entirely until the async name is available to avoid raw-UID flash
    // and layout shift for rows without a target.
    if (!name) return null

    const pillClass =
      'inline-flex items-center gap-1 rounded-md bg-muted/60 hover:bg-muted text-muted-foreground hover:text-foreground text-xs px-1.5 py-0.5 max-w-[200px] truncate transition-colors'

    const content = (
      <>
        <AppIcon path={icon} size="xs" className="flex-shrink-0" />
        <span className="truncate">{name}</span>
      </>
    )

    if (to && params) {
      return (
        <Link
          to={to}
          params={params}
          search={search}
          className={`${pillClass} ${className ?? ''}`}
          data-testid="issue-target-compact"
          onClick={(e) => {
            // Issue rows are clickable containers (a div with role="link", never an
            // anchor — `<a>` inside `<a>` is invalid HTML) that navigate to the
            // ticket. The pill goes to its own target instead, so stop the click
            // from bubbling up to the row.
            e.stopPropagation()
          }}
        >
          {content}
        </Link>
      )
    }
    return (
      <span className={`${pillClass} ${className ?? ''}`} data-testid="issue-target-compact">
        {content}
      </span>
    )
  }

  // Never print a raw UID: while the name loads, or when the read fails, the
  // link reads as the kind of thing it points at.
  const label = name || issueTargetLabel(type, undefined, t)
  const linkEl =
    to && params ? (
      <Link
        to={to}
        params={params}
        search={search}
        className="hover:underline"
        data-testid="issue-target-link"
      >
        {label}
      </Link>
    ) : (
      <span data-testid="issue-target-link">{label}</span>
    )

  return (
    <div className={`flex items-start gap-2 text-sm ${className ?? ''}`} data-testid="issue-target">
      <AppIcon path={icon} size="sm" className="mt-0.5 flex-shrink-0" />
      <div className="min-w-0 text-foreground truncate">{linkEl}</div>
    </div>
  )
}
